import React, { useState, useEffect } from "react"
import { ExtractId } from "../../../util"
import Paginator from "../../Paginator"
import { getManageAccountList } from "../../../api/manage_account_list"
import { useNavigate } from "react-router-dom"

import ManageAccountCatvCreate from "./catv/create"
import ManageAccountCatvUpdate from "./catv/update"
import ManageAccountCatvDelete from "./catv/delete"
import ManageAccountEstateCreate from "./estate/create"
import ManageAccountEstateUpdate from "./estate/update"
import ManageAccountEstateDelete from "./estate/delete"
import ManageAccountResetPassword from "./reset_password"
import { useLoading } from "../../LoadingContext"
import { useConnectionError } from "../../ConnectionErrorContext"

import DashboardLayout from "../../components/DashboardLayout"
import DashboardNavbar from "../../components/DashboardNavbar"
import Icon from "@mui/material/Icon"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Button from "@mui/material/Button"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"
import { Checkbox, FormControlLabel } from "@mui/material"
import { keyframes } from "@emotion/react"

export interface ManageAccountData {
  id: string
  catv_id: string | ""
  estate_id: string | ""
  type: string
  name: string
  mail: string
  session_date: number | 0 // 秒単位
  auth_account: number | 0
  auth_list: number | 0
  auth_setting: number | 0
  auth_logo: number | 0
  auth_facility: number | 0
  create_date: number
  update_date: number
  is_delete: number
  lock: number | 0
  send_password_reset: number | 0
}

export interface ManageEstateData {
  id: string
  catv_id: string
  type: string
  name: string
  address: string
  phone: string
  create_date: number
  update_date: number
  is_delete: number
}

const ManageAccountTileList: React.FC = () => {
  const { startLoading, stopLoading } = useLoading()
  const { showConnectionError } = useConnectionError()
  const navigate = useNavigate()

  const [searchText, setSearchText] = useState("")
  const [showNoDelete, setShowNoDelete] = useState<boolean>(true)
  const [errorMessage, setErrorMessage] = useState("")
  const [accountData, setAccountData] = useState<ManageAccountData[]>([])
  const [filteredData, setFilteredData] = useState<ManageAccountData[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 25

  const [showAccountCatv, setShowAccountCatv] = useState<boolean>(false)
  const [accountCatvCreate, setAccountCatvCreate] = useState<boolean>(false)
  const [accountCatvUpdate, setAccountCatvUpdate] = useState<boolean>(false)
  const [accountCatvDelete, setAccountCatvDelete] = useState<boolean>(false)
  const [showAccountEstateCreate, setShowAccountEstateCreate] = useState<boolean>(false)
  const [showAccountEstateUpdate, setShowAccountEstateUpdate] = useState<boolean>(false)
  const [showAccountEstateDelete, setShowAccountEstateDelete] = useState<boolean>(false)
  const [accountEstateCreate, setAccountEstateCreate] = useState<boolean>(false)
  const [accountEstateUpdate, setAccountEstateUpdate] = useState<boolean>(false)
  const [accountEstateDelete, setAccountEstateDelete] = useState<boolean>(false)
  const [accountResetPassword, setResetPassword] = useState<boolean>(false)
  const [selectedAccount, setSelectedAccount] = useState<ManageAccountData | null>(null)
  // アニメーションの定義
  const blinkAnimation = keyframes`
    0% {
      background-color: #ffffff;
    }
    50% {
      background-color: #ffeeee;
    }
    100% {
      background-color: #ffffff;
    }
  `

  // 開発者アカウントでの閲覧を可能とする
  const [isViewMode, setIsViewMode] = useState<boolean>(false)

  if (localStorage.getItem("session_code") == "") {
    navigate("/login")
    return <></>
  }

  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  const fetchManageAccountList = async () => {
    try {
      startLoading()
      const manageAccountData = await getManageAccountList()
      setAccountData(() => [...manageAccountData.account_list])
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        navigate("/login")
      } else if (!error.response) {
        showConnectionError(true)
      }
    } finally {
      stopLoading()
    }
  }

  useEffect(() => {
    const auth = localStorage.getItem("account_type")
    if (auth == "service") {
      const view_mode = localStorage.getItem("view_mode")
      if (view_mode == "2") {
        const catv_id = localStorage.getItem("show_mode_catv")
        const estate_id = localStorage.getItem("show_mode_estate")
        if (estate_id !== "" && estate_id !== "-") {
          setShowAccountEstateUpdate(true)
        } else if (catv_id !== "" && catv_id !== "-") {
          setShowAccountEstateCreate(true)
          setShowAccountEstateUpdate(true)
          setShowAccountEstateDelete(true)
        } else {
          setShowAccountCatv(true)
        }
      } else if (view_mode == "1") {
        setIsViewMode(true)
      } else {
        setShowAccountCatv(true)
      }
    } else if (auth == "catv") {
      const auth_account = localStorage.getItem("auth_account")
      if (auth_account === "1") {
        setShowAccountEstateCreate(true)
        setShowAccountEstateUpdate(true)
        setShowAccountEstateDelete(true)
      } else {
        const auth_setting = localStorage.getItem("auth_setting")
        if (auth_setting === "1") {
          setShowAccountEstateUpdate(true)
        }
      }
    } else {
      const auth_setting = localStorage.getItem("auth_setting")
      if (auth_setting === "1") {
        setShowAccountEstateUpdate(true)
      }
    }
    fetchManageAccountList()
  }, [])

  // 検索処理
  const handleSearch = () => {
    const filtered = accountData.filter((account) => {
      const matchesText = searchText === "" || account.name?.includes(searchText) || account.mail?.includes(searchText)

      const matchesAlert = !showNoDelete || account.is_delete == 0
      return matchesText && matchesAlert
    })
    setFilteredData(filtered)
    setCurrentPage(1)
  }

  // 初期表示時に初期ページを表示
  useEffect(() => {
    handleSearch()
  }, [accountData])

  const handleEdit = (id: string) => {
    //console.log("編集ボタンがクリックされた " + id);
    const accountToEdit = accountData.find((account) => account.id === id)
    if (accountToEdit) {
      if (showAccountCatv) {
        setSelectedAccount(accountToEdit) // 編集対象をセット
        setAccountCatvUpdate(true) // 編集モーダルを表示
      } else if (showAccountEstateUpdate) {
        setSelectedAccount(accountToEdit) // 編集対象をセット
        setAccountEstateUpdate(true) // 編集モーダルを表示
      }
    }
  }
  const handleDelete = (id: string) => {
    //console.log("削除ボタンがクリックされた " + id);
    const accountToEdit = accountData.find((account) => account.id === id)
    if (accountToEdit) {
      if (showAccountCatv) {
        setSelectedAccount(accountToEdit) // 編集対象をセット
        setAccountCatvDelete(true) // 編集モーダルを表示
      } else if (showAccountEstateUpdate) {
        setSelectedAccount(accountToEdit) // 編集対象をセット
        setAccountEstateDelete(true) // 編集モーダルを表示
      }
    }
  }
  const handlePasswordReset = (id: string) => {
    //console.log("パスワード初期化ボタンがクリックされた " + id);
    const accountToEdit = accountData.find((account) => account.id === id)
    if (accountToEdit) {
      setSelectedAccount(accountToEdit) // 編集対象をセット
      setResetPassword(true) // 編集モーダルを表示
    }
  }

  const totalPages = Math.ceil(filteredData.length / itemsPerPage)

  const handleCatvCreate = () => {
    //console.log("追加ボタンがクリックされた ");
    setAccountCatvCreate(true)
  }

  const handleEstateCreate = () => {
    //console.log("追加ボタンがクリックされた ");
    setAccountEstateCreate(true)
  }

  const handleClose = async (num: number) => {
    if (num === 1) {
      // APIを再取得
      await fetchManageAccountList()

      // データ更新後に検索を行う
      setAccountCatvCreate(false)
      setAccountCatvUpdate(false)
      setAccountCatvDelete(false)

      setAccountEstateCreate(false)
      setAccountEstateUpdate(false)
      setAccountEstateDelete(false)

      setResetPassword(false)
    } else {
      setAccountCatvCreate(false)
      setAccountCatvUpdate(false)
      setAccountCatvDelete(false)

      setAccountEstateCreate(false)
      setAccountEstateUpdate(false)
      setAccountEstateDelete(false)

      setResetPassword(false)
    }
  }

  const getLoginStatus = (sessionDate: number): string => {
    const nowInSeconds = Math.floor(Date.now() / 1000) // 現在時刻を秒単位に変換
    const oneHourInSeconds = 60 * 60 // 1時間を秒単位で表現
    return nowInSeconds - (sessionDate + 9 * 60 * 60) <= oneHourInSeconds ? "ログイン状態" : "未ログイン状態"
  }

  // CSVダウンロード処理
  const handleDownloadCSV = () => {
    const headers = ["ユーザー名", "メールアドレス", "ロック状態", "削除（停止）状態"]

    const csvContent = [
      headers.join(","),
      ...accountData.map((item) => {
        const userName = item?.name || ""
        const mail = item?.mail || ""
        const lock = item?.lock ? "ロック中" : ""
        const isDelete = item?.is_delete ? "削除済" : ""

        return [userName, mail, lock, isDelete].join(",")
      })
    ].join("\n")

    const utf8Bom = "\uFEFF"
    const blob = new Blob([utf8Bom + csvContent], { type: "text/csv;charset=utf-8;" })

    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "account_list.csv"
    link.click()
    URL.revokeObjectURL(url)
  }

  const validate = (message: string) => {
    setSearchText(message)

    if (message.length > 10) {
      setErrorMessage("入力文字数は10文字以内です")
    } else {
      setErrorMessage("") // エラーがない場合はクリア
    }
  }

  // session_date が Unix タイムスタンプ (秒 or ミリ秒) だと仮定
  const threeMonthAgo = new Date()
  // 最終のログインから3ヵ月以上経過しているかどうかの判断用数値
  threeMonthAgo.setMonth(threeMonthAgo.getMonth() - 3)

  return (
    <DashboardLayout>
      {accountCatvCreate && <ManageAccountCatvCreate onClose={handleClose} />}
      {accountCatvUpdate && <ManageAccountCatvUpdate onClose={handleClose} accountData={selectedAccount} />}
      {accountCatvDelete && <ManageAccountCatvDelete onClose={handleClose} accountData={selectedAccount} />}
      {accountEstateCreate && <ManageAccountEstateCreate onClose={handleClose} />}
      {accountEstateUpdate && <ManageAccountEstateUpdate onClose={handleClose} accountData={selectedAccount} />}
      {accountEstateDelete && <ManageAccountEstateDelete onClose={handleClose} accountData={selectedAccount} />}
      {accountResetPassword && <ManageAccountResetPassword onClose={handleClose} accountData={selectedAccount} />}
      <MDBox mb={4}>
        <DashboardNavbar absolute={true} />
      </MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={3} pb={3}>
        <MDBox>
          <MDTypography variant="h4" gutterBottom>
            アカウント一覧
          </MDTypography>
        </MDBox>
      </MDBox>
      {/* 検索フォーム */}
      <MDBox
        mb={2}
        display="flex"
        gap={3}
        flexWrap="wrap"
        alignItems="center"
        sx={{ fontSize: "0.875rem", color: "text.primary" }}
      >
        <MDBox display="flex" alignItems="center" gap={1}>
          <div style={{ position: "relative", display: "inline-block" }}>
            <MDInput
              label="ユーザー名やメールアドレスで検索"
              value={searchText}
              onChange={(e) => validate(e.target.value)}
              sx={{ width: "300px", backgroundColor: "#FFFFFF", borderRadius: "10px" }}
              error={Boolean(errorMessage)} // エラー時のスタイル変更
            />
            {errorMessage && (
              <div
                style={{
                  position: "absolute",
                  top: "100%", // 入力欄の下に配置
                  left: "0",
                  fontSize: "12px",
                  color: "red",
                  marginTop: "4px",
                  whiteSpace: "nowrap"
                }}
              >
                {errorMessage}
              </div>
            )}
          </div>
        </MDBox>
        <MDBox display="flex" alignItems="center" gap={1}>
          <FormControlLabel
            control={<Checkbox checked={showNoDelete} onChange={(e) => setShowNoDelete(e.target.checked)} />}
            label="削除済を除外"
          />
        </MDBox>
        <MDButton
          variant="gradient"
          color="info"
          size="medium"
          onClick={handleSearch}
          disabled={errorMessage !== ""} // エラーメッセージがある場合は無効化
        >
          <Icon>search</Icon>&nbsp; 検索
        </MDButton>
        {isViewMode && <MDBox sx={{ width: "calc(100% - 722px)" }} />}
        {!isViewMode && (showAccountCatv || showAccountEstateCreate) && <MDBox sx={{ width: "calc(100% - 820px)" }} />}
        {!isViewMode && !(showAccountCatv || showAccountEstateCreate) && <MDBox sx={{ width: "calc(100% - 724px)" }} />}
        <MDBox>
          <MDButton variant="outlined" color="info" size="medium" onClick={handleDownloadCSV}>
            <Icon>download</Icon>&nbsp; CSV
          </MDButton>
        </MDBox>
        {!isViewMode && (
          <>
            {showAccountCatv && (
              <MDBox>
                <MDButton variant="gradient" color="info" size="medium" onClick={handleCatvCreate}>
                  追加
                </MDButton>
              </MDBox>
            )}
            {showAccountEstateCreate && (
              <MDBox>
                <MDButton variant="gradient" color="info" size="medium" onClick={handleEstateCreate}>
                  追加
                </MDButton>
              </MDBox>
            )}
          </>
        )}
      </MDBox>

      {/* タイルの表示 */}
      <MDBox mt={4}>
        <TableContainer sx={{ overflowX: "auto", overflowY: "auto", maxHeight: "calc(100vh - 300px)" }}>
          <Table stickyHeader>
            <TableHead sx={{ position: "sticky", display: "table-header-group", zIndex: 3 }}>
              <TableRow
                sx={{
                  "& .MuiTableCell-root": {
                    fontSize: "0.875rem",
                    color: "text.primary",
                    fontWeight: "bold",
                    backgroundColor: "#FFFFFF"
                  }
                }}
              >
                <TableCell align="left">ユーザーID</TableCell>
                <TableCell align="left">ユーザー名</TableCell>
                <TableCell align="left">メールアドレス</TableCell>
                <TableCell align="left">ログイン状態</TableCell>
                <TableCell align="left">削除済</TableCell>
                {!isViewMode && (
                  <>
                    <TableCell align="center" style={{ minWidth: "75px" }}>
                      編集
                    </TableCell>
                    <TableCell align="center" style={{ minWidth: "75px" }}>
                      削除
                    </TableCell>
                    <TableCell align="center" style={{ minWidth: "100px" }}>
                      パスワード初期化
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "& .MuiTableCell-root": {
                      fontSize: "0.875rem",
                      color: "text.primary",
                      fontWeight: "normal",
                      zIndex: 0
                    },
                    animation:
                      row.session_date * 1000 < threeMonthAgo.getTime()
                        ? `${blinkAnimation} 2s infinite alternate ease-in-out`
                        : "none"
                  }}
                >
                  <TableCell align="left">
                    <div
                      style={{
                        minWidth: "50px",
                        maxWidth: "300px",
                        width: "10vw",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                      title={ExtractId(row.id) || undefined}
                    >
                      {ExtractId(row.id)}
                    </div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "150px" }}>{row.name}</div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "100px" }}>{row.mail}</div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "100px" }}>{getLoginStatus(row.session_date)}</div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "50px" }}>{row.is_delete == 1 ? "削除済" : ""}</div>
                  </TableCell>
                  {!isViewMode && (
                    <>
                      <TableCell align="right">
                        {(showAccountCatv || showAccountEstateUpdate) && row.is_delete == 0 && (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            sx={{ color: "#FFF" }}
                            onClick={() => {
                              handleEdit(`${row.id}`)
                            }}
                          >
                            編集
                          </Button>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {(showAccountCatv || showAccountEstateDelete) && row.is_delete == 0 && (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            sx={{ color: "#FFF" }}
                            onClick={() => {
                              handleDelete(`${row.id}`)
                            }}
                          >
                            削除
                          </Button>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {row.send_password_reset == 1 && row.is_delete == 0 && (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            sx={{ color: "#FFF", width: "136px" }}
                            onClick={() => {
                              handlePasswordReset(`${row.id}`)
                            }}
                          >
                            パスワード初期化
                          </Button>
                        )}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
      {/* ページャー */}
      <Paginator
        key={"ManageAccountTileListPaginator"}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </DashboardLayout>
  )
}

export default ManageAccountTileList
